import { useStore } from '@/store'
import { closeLoading, loading } from '@/utils'
import cloneDeep from 'lodash/cloneDeep'
import { useCallback, useEffect, useRef, useState } from 'react'

export default function useRequest<
  T extends (...args: unknown[]) => Promise<any>
>(request: T, _defaultValue?: Awaited<ReturnType<T>>, deps?: unknown) {
  const [data, setData] = useState<typeof _defaultValue | null>(
    getDefaultValue()
  )
  const [err, setErr] = useState<unknown>(null)
  const { state } = useStore()
  const [pending, setPending] = useState(false)
  const isBinded = state.login.userInfo.isBinded
  const innerPending = useRef(false)
  const status = useRef<'Pending' | 'Fulfilled' | 'Rejected'>('Pending')
  const cache = useRef<typeof data>(null)

  const fetchData = () => {
    status.current = 'Pending'
    innerPending.current = true
    loading()
    setPending(true)
    setData(getDefaultValue())
    setErr(null)
    request()
      .then((res) => {
        setData(res)
        setErr(null)
        cache.current = res
        status.current = 'Fulfilled'
      })
      .catch((error) => {
        setData(getDefaultValue())
        setErr(error)
        cache.current = null
        status.current = 'Rejected'
      })
      .finally(() => {
        closeLoading()
        innerPending.current = false
        setPending(false)
      })
  }

  const update = useCallback(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (status.current === 'Fulfilled') return setData(cache.current)

    if (innerPending.current) return
    fetchData()
  }, [deps])

  function getDefaultValue() {
    return _defaultValue ? cloneDeep(_defaultValue) : null
  }

  return [data, err, pending, update] as const
}
